import { useEffect, useLayoutEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";

import AppDateTimePicker from "../../../../../components/common/forms/AppDateTimePicker";
import AppTextField from "../../../../../components/common/forms/AppTextField";
import AppDatePicker from "../../../../../components/common/forms/AppDatePicker";
import AppSelect from "../../../../../components/common/forms/AppSelect";
import { useGetEquipmentsQuery } from "../../../../../hooks/useEquipments";
import { appColors } from "../../../../../theme";
import { Grid } from "@mui/material";
import { useAuth } from "../../../../../components/AuthProvider";
import {
  useGetESRByIDForApproval,
  useUpdateESR,
} from "../../../../../hooks/useESRApproval";
import { ESRFormFields, UpdateESRRequestBody } from "./utils/types";
import { ESRApprovalInitialValues } from "./utils/schema";
import ESRManpowerTable from "./ESR-Manpower/ESRManpowerTable";
import ESREquipmentServicesTable from "./ESR-EquipmentService/ESREquipmentServiceTable";
import LoadingIndicator from "../../../../../components/common/LoadingIndicator";
import { useESRApprovalContext } from "../Context/ESRApprovalContext";
import EmailSendingForm from "./Email-Parts-List/EmailSendingForm";
import ESREquipmentServiceForm from "./ESR-EquipmentService/ESREquipmentServiceForm";
import ESRManpowerForm from "./ESR-Manpower/ESRManpowerForm";
import { useSnackBar } from "../../../../../components/ToastProvider";
import {
  ESRManpowerColumns,
  ESRManpowerFormFields,
} from "./ESR-Manpower/utils/types";
import {
  ESREquipmentServiceColumns,
  ESREquipmentServiceFormFields,
} from "./ESR-EquipmentService/utils/types";

const ESRApprovalForm = () => {
  const { id: paramsId } = useParams();
  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();
  const location = useLocation();
  const updateESR = useUpdateESR();
  const currUser = useAuth();
  const equipmentData = useGetEquipmentsQuery();
  const { data: currentESRSelected, isLoading: isESRLoading } =
    useGetESRByIDForApproval();

  const {
    openEmailSendingDialog,
    setOpenEmailSendingDialog,
    setEsrManpowerArray,
    setEsrEquipmentServicesArray,
    esrActionType,
    setESRActionType,
    esrEquipmentServicesArray,
    esrManpowerArray,
    esrArray,
  } = useESRApprovalContext();
  const {
    register,
    control,
    setValue,
    getValues,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<ESRFormFields>({
    defaultValues: ESRApprovalInitialValues,
    mode: "onChange",
  });
  
  const [tmpDateStarted, setTmpDateStarted] =  useState<any>(new Date);
	const [tmpDateCompleted, setTmpDateCompleted] = useState<any>(new Date);
  const [tmpNotes, setTmpNotes] = useState<any>(currentESRSelected?.esrNotes ?? "");
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const rolePermissions = currUser && currUser.currentUserRole?.permissions;
  const equipmentOptions: any[] =
    equipmentData?.data === undefined
      ? []
      : equipmentData.data.map((e) => {
          return {
            id: e.id,
            label: e.equipmentNo,
            value: e.id,
          };
        });

  const onSubmit = (esrData: ESRFormFields) => {
    if (esrManpowerArray.length === 0) {
      showSnackBar("Manpower & Man Hour is required", "error");
      return;
    }
    if (esrEquipmentServicesArray.length === 0) {
      showSnackBar("Task & Part Number List is required", "error");
      return;
    }

    if (!currentESRSelected) return;

    const formattedESRManHourList: ESRManpowerFormFields[] =
      esrManpowerArray.map((mh: ESRManpowerColumns) => {
        const isExistingInDb = mh.id.length > 5;

        const newMh: ESRManpowerFormFields = {
          esrId: currentESRSelected.id,
          id: mh.id,
          manHours: mh.manHours,
          mechanicId: mh.mechanicId,
        };

        if (!isExistingInDb) {
          const { id, ...rest } = newMh;
          return { id: "", ...rest };
        }

        return newMh;
      });

    const formattedESREquipmentServiceList: ESREquipmentServiceFormFields[] =
      esrEquipmentServicesArray.map((es: ESREquipmentServiceColumns) => {
        const isExistingInDb = es.id.length > 5;

        const newEs = {
          esrId: currentESRSelected.id,
          id: es.id,
          intervalType: "Periodic",
          partNumber: es.partNumber,
          partsDescriptionId: "",
          quantity: es.quantity,
          remarks: es.remarks,
          taskItem: es.taskItem,
          taskTypeId: es.taskTypeId,
        };

        if (!isExistingInDb) {
          const { id, ...rest } = newEs;
          return { id: "", ...rest };
        }

        return newEs;
      });

      const updateESRFormatForUpdate: UpdateESRRequestBody = {
        esrNumber: esrData.esrNumber,
        serviceRequestId: currentESRSelected.serviceRequestId,
        serviceRequestTypeId: currentESRSelected.serviceRequestTypeId,
        equipmentId: esrData.equipmentId,
        status: "",
        esrManHourList: formattedESRManHourList,
        equipmentServiceList: formattedESREquipmentServiceList,
      };
      if (esrActionType == "approval") {
        updateESRFormatForUpdate.status = "Approved";
        delete updateESRFormatForUpdate.esrManHourList;
        delete updateESRFormatForUpdate.equipmentServiceList;
        updateESR.mutate(updateESRFormatForUpdate);
      }
      if (esrActionType == "starting") {
        updateESRFormatForUpdate.status = "Started";
        updateESR.mutate(updateESRFormatForUpdate);
      }
      if (esrActionType == "completion") {
		  updateESRFormatForUpdate.status = "Completed";
	
      if(!tmpDateStarted || !tmpDateCompleted){
        showSnackBar("Start Date and Completion Date are required", "error"); return;
      } 

      if(new Date(tmpDateStarted) >new Date(tmpDateCompleted)){
        showSnackBar("Completion date must be later than start date", "error"); 
        return;
      }
      updateESRFormatForUpdate.dateStarted = tmpDateStarted.toISOString().replace('Z', '');;
      updateESRFormatForUpdate.dateCompleted = tmpDateCompleted.toISOString().replace('Z', '');;
      updateESRFormatForUpdate.esrNotes = esrData?.esrNotes ?? "";
      updateESR.mutate(updateESRFormatForUpdate);
    }
  };

  const backTo = () => {
    //* To clear the fields
    reset();
    navigate(`/${pathSegments[0]}/${pathSegments[1]}`);
  };

  useEffect(() => {
    //* Set the action type to give each action different validation if needed
    if (pathSegments[2] == "approve") {
      setESRActionType("approval");
    }

    if (pathSegments[2] == "view") {
      setESRActionType("viewing");
    }

    if (pathSegments[2] == "start") {
      setESRActionType("starting");
    }

    if (pathSegments[2] == "complete") {
      setESRActionType("completion");
    }

    if (paramsId) {
      if (currentESRSelected) {
        // TODO GIVE TYPE OF ESR FORM FIELDS
        const valuesOfCurrentESRSelected: ESRFormFields = {
          id: currentESRSelected.id,
          esrNumber: currentESRSelected.esrNumber,
          equipmentId: currentESRSelected.equipmentId,
          dateNeeded: currentESRSelected.dateNeeded,
          dateCreated: currentESRSelected.dateCreated,
          serviceRequested: String(currentESRSelected?.serviceRequestType),
          remarks: currentESRSelected.remarks,
          pmCode: currentESRSelected.pmCode,
          isWarrantyJob: currentESRSelected?.isWarrantyJob,
        };

        //* Set the array for further editing
        setEsrManpowerArray(currentESRSelected.esrManHourList);
        setEsrEquipmentServicesArray(currentESRSelected.equipmentServiceList);

        //* Use reset to set the form values to the esr current values
        reset(valuesOfCurrentESRSelected);
      }
    }
  }, [currentESRSelected, paramsId, setValue]);

  if (isESRLoading) {
    if (paramsId) {
      return <LoadingIndicator show />;
    }
  }

  //* Used for validating. Did not use isValid because isValid still return false even though the data is valid
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <>
      <Stack direction="row" gap={2} justifyContent={"flex"} mb={1}>
        <Button
          sx={{
            backgroundColor: appColors.lightGray,
            color: "black",
            marginLeft: "auto",
          }}
          disabled={updateESR.isLoading}
          onClick={backTo}
        >
          {esrActionType == "viewing" ? "Back" : "Cancel"}
        </Button>

        {esrActionType != "viewing" && (
          <>
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={hasErrors || updateESR.isLoading || updateESR.isSuccess}
              disableElevation
              variant="contained"
              sx={{ textTransform: "capitalize", borderRadius: 1.5 }}
            >
              {!updateESR.isLoading && esrActionType == "approval"
                ? "Approve"
                : null}
              {updateESR.isLoading && esrActionType == "approval"
                ? "Approving"
                : null}
              {!updateESR.isLoading && esrActionType == "starting"
                ? "Start"
                : null}
              {updateESR.isLoading && esrActionType == "starting"
                ? "Starting..."
                : null}
              {!updateESR.isLoading && esrActionType == "completion"
                ? "Complete"
                : null}
              {updateESR.isLoading && esrActionType == "completion"
                ? "Completing..."
                : null}
            </Button>
          </>
        )}
      </Stack>
      <Divider color="black" />
      <Grid container spacing={2} sx={{ marginTop: "1em" }}>
        {/* TITLE EJO NUMBER */}
        <Grid container spacing={2} sx={{ marginLeft: ".14em" }}>
          <Grid item sm={12} md={12} lg={6}>
            <AppTextField
              label="JO NO."
              labelAlignment="left"
              labelWidth="25%"
              fieldWidth="75%"
              fontWeight={400}
              placeholder="0"
              value={currentESRSelected?.esrNumber}
              disabled={true}
            />
          </Grid>
          <Grid item sm={12} md={12} lg={6}>
            <AppTextField
              label="Business Unit"
              labelAlignment="left"
              labelWidth="25%"
              fieldWidth="75%"
              fontWeight={400}
              placeholder="0"
              value={currentESRSelected?.businessUnit.name}
              disabled={true}
            />
          </Grid>
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <AppDatePicker
            name="dateCreated"
            value={currentESRSelected?.dateCreated ?? new Date()}
            onChange={() => {}}
            label="Date Created"
            labelAlignment="left"
            labelWidth="25%"
            fieldWidth="75%"
            fontWeight={400}
            disabled={true}
          />
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <Controller
            control={control}
            name="dateNeeded"
            render={({ field: { value, onChange } }) => (
              <AppDatePicker
                name="dateNeeded"
                value={new Date(value)}
                onChange={onChange}
                label="Date Needed"
                labelAlignment="left"
                labelWidth="25%"
                fieldWidth="75%"
                fontWeight={400}
                error={!!errors.dateNeeded}
                helperText={errors.dateNeeded?.message}
                minDate={new Date()}
                disabled={true}
              />
            )}
          />
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <Controller
            control={control}
            name="equipmentId"
            render={({ field }) => (
              <AppSelect
                label="Equipment No."
                labelAlignment="left"
                labelWidth="25%"
                fieldWidth="75%"
                fontWeight={400}
                placeholder="Select here"
                error={!!errors.equipmentId}
                helperText={errors.equipmentId?.message}
                options={equipmentOptions}
                {...register("equipmentId")}
                {...field}
                disabled={true}
              />
            )}
          />
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <AppTextField
            label="Service Requested"
            labelAlignment="left"
            labelWidth="25%"
            fieldWidth="75%"
            fontWeight={400}
            placeholder="0"
            value={currentESRSelected?.serviceRequestType}
            disabled={true}
          />
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <AppTextField
            label={!currentESRSelected?.remarks ? "PM Code" : "Remarks"}
            labelAlignment="left"
            labelWidth="25%"
            fieldWidth="75%"
            fontWeight={400}
            placeholder="0"
            value={
              !currentESRSelected?.remarks
                ? currentESRSelected?.pmCode
                : currentESRSelected?.remarks
            }
            disabled={true}
          />
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <Controller
              control={control}
              name="esrNotes"
              defaultValue={currentESRSelected?.esrNotes || ''} 
              render={({ field }) => (
                <AppTextField
                  label="Notes"
                  labelAlignment="left"
                  labelWidth="25%"
                  fieldWidth="75%"
                  fontWeight={400}
                  placeholder="Notes"
                  {...register("esrNotes")}
                  {...field}
                  disabled= { esrActionType == "completion" ? false : true}
                />
              )}
          />
        </Grid>
        {pathSegments[1] == "esr-wip" && esrActionType =="completion" && (
          <>
            <Grid item sm={12} md={12} lg={6}>
              <AppDateTimePicker
              name="dateStarted"
              value={ tmpDateStarted ?? new Date()  }
              onChange={ (value) => setTmpDateStarted(value) }
              label="Start Date"
              labelAlignment="left"
              labelWidth="25%"
              fieldWidth="75%"
              fontWeight={400}
              />
            </Grid>
            <Grid item sm={12} md={12} lg={6}>
              <AppDateTimePicker
                name="dateCompleted"
                value={ tmpDateCompleted ??  new Date() }
                onChange={ (value) => setTmpDateCompleted(value) }
                label="Completion Date"
                labelAlignment="left"
                labelWidth="25%"
                fieldWidth="75%"
                fontWeight={400}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <ESRManpowerTable />
        </Grid>
        <Grid item xs={12}>
          <ESREquipmentServicesTable />
        </Grid>
      </Grid>
      <ESRManpowerForm />
      <ESREquipmentServiceForm />
      {openEmailSendingDialog && (
        <EmailSendingForm
          esrNumber={String(currentESRSelected?.esrNumber)}
          equipmentNo={String(currentESRSelected?.equipmentNo)}
        />
      )}
    </>
  );
};

export default ESRApprovalForm;
